import React from "react";
import "./ChampionPanel.css";

type Team = "BLUE" | "RED";
type Role = "TOP" | "JGL" | "MID" | "ADC" | "SUP";
type SpellKey = "Q" | "W" | "E" | "R" | "A";

type Unit = {
  id: string;
  team: Team;
  role: Role;
  x: number;
  y: number;
  range: number;
  health: number;
  maxHealth: number;
};

type SpellDef = {
  key: SpellKey;
  name: string;
  range: number;
  damage: number;
  kind: "aoe" | "beam";
  radius?: number;
};

interface ChampionPanelProps {
  unit: Unit | null;
  target: Unit | null;
  spells: Record<SpellKey, SpellDef> | null;
  onClickSpell: (k: SpellKey) => void;
}

const TEAM_BG: Record<Team, string> = { BLUE: "#3B82F6", RED: "#EF4444" };
const roleShort = (role: Role) => (role === "JGL" ? "JG" : role);

export default function ChampionPanel({ unit, target, spells, onClickSpell }: ChampionPanelProps) {
  return (
    <div className="tf-card tf-champion-panel">
      {!unit ? (
        <div className="tf-champion-panel__empty">Select a unit to view details.</div>
      ) : (
        <div>
          <div className="tf-champion-panel__header">
            <div className="tf-champion-panel__avatar" />
            <div>
              <div className="tf-champion-panel__name">Champion ({roleShort(unit.role)})</div>
              <div className="tf-champion-panel__meta">
                {unit.team} • HP: {unit.health}/{unit.maxHealth} • Range {unit.range}
              </div>
            </div>
          </div>

          {target && (
            <div className="tf-champion-panel__target">
              <div className="tf-champion-panel__section-title">Target</div>
              <div className="tf-champion-panel__target-info">
                <div 
                  className="tf-champion-panel__target-indicator" 
                  style={{ backgroundColor: TEAM_BG[target.team] }} 
                />
                <div className="tf-champion-panel__target-name">
                  {roleShort(target.role)} ({target.team})
                </div>
                <div className="tf-champion-panel__target-health">
                  HP: {target.health}/{target.maxHealth}
                </div>
              </div>
            </div>
          )}

          <div className="tf-champion-panel__section">
            <div className="tf-champion-panel__section-title">Spells</div>
            <div className="tf-champion-panel__spells">
              {spells && (["Q", "W", "E", "R", "A"] as SpellKey[]).map((k) => {
                const spell = spells[k];
                return (
                  <button 
                    className="tf-champion-panel__spell" 
                    key={k} 
                    onClick={() => onClickSpell(k)} 
                    title={`Cast ${k} - Range: ${spell.range}, Damage: ${spell.damage}`}
                  >
                    <div className="tf-champion-panel__spell-icon">{k}</div>
                    <div className="tf-champion-panel__spell-details">
                      <div className="tf-champion-panel__spell-name">{spell.name}</div>
                      <div className="tf-champion-panel__spell-stats">R:{spell.range} D:{spell.damage}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="tf-champion-panel__section">
            <div className="tf-champion-panel__section-title">Runes</div>
            <div className="tf-champion-panel__runes">
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="tf-champion-panel__rune" key={i} />
              ))}
            </div>
          </div>

          <div className="tf-champion-panel__section">
            <div className="tf-champion-panel__section-title">Items</div>
            <div className="tf-champion-panel__items">
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="tf-champion-panel__item" key={i} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export type { Unit, SpellDef, SpellKey, Team, Role };
